import React, { useContext, useEffect, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Button,
  Grid,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useHistory, useRouteMatch } from "react-router-dom";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import DeletedOrgsSearch from "../components/search/deleted-orgs-search";
import DocSearch from "../components/search/doc-search";
import IdentitySearch from "../components/search/identity-search";
import MediaSearch from "../components/search/media-search";
import OrgSearch from "../components/search/org-search";
import PublicLibrarySearch from "../components/search/public-library-search";
import UserSearch from "../components/search/user-search";
import TabPanel from "../components/shared/tab-panel";
import { OrgSearchQueryContext } from "../context/org-search-context";
import { PermissionsContext } from "../context/permissions-context";
import { SearchTabContext } from "../context/search-context";
import { UserSearchQueryContext } from "../context/user-search-context";
import useNavRedesign from "../helpers/hooks/use-nav-redesign";
import useQuery from "../helpers/hooks/use-query";
import mainStyles from "../styles";
import ContentLibrarySearch from "components/search/content-library-search";
import GrsSearch from "components/search/grs-search";
import SensorsSearch from "components/search/sensors-search";
import TemplateSearch from "components/search/template-search";
import { GrsSearchQueryContext } from "context/grs-search-context";
import { IdentitySearchQueryContext } from "context/identity-search-context";
import { TemplateSearchQueryContext } from "context/template-search-context";

interface TabMatchParams {
  tab: string;
}

const Search: React.FC = () => {
  const query = useQuery();

  const classes = mainStyles();
  const { searchTab, setSearchTab, docSearchKey, userSearchKey, orgSearchKey } =
    useContext(SearchTabContext);
  const history = useHistory();
  const navRedesign = useNavRedesign();
  const { hasPermissions } = useContext(PermissionsContext);
  // Deleted-org records are customer PII; only show the tab to operators granted
  // the action (FG-8007). Optional-chained so it stays safe where the
  // permissions provider isn't mounted (e.g. unit tests).
  const canSearchDeletedOrgs =
    hasPermissions?.([ActionType.SEARCH_DELETED_ORGS]) ?? false;
  const [scopeMenuAnchor, setScopeMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  // Only collapse scopes into a "+N more" menu when the viewport is too narrow
  // to show them all inline.
  const wideEnoughForAllScopes = useMediaQuery("(min-width:1400px)");

  // Clear the "+N more" menu anchor when the menu is no longer rendered (all
  // scopes inline, or redesign disabled) so it can't reopen on a stale node.
  useEffect(() => {
    if (wideEnoughForAllScopes || !navRedesign) {
      setScopeMenuAnchor(null);
    }
  }, [wideEnoughForAllScopes, navRedesign]);

  const tabs = [
    {
      key: "user",
      label: "Users",
      component: <UserSearch key={userSearchKey} />,
      enabled: true,
    },
    {
      key: "identity",
      label: "Identity",
      component: <IdentitySearch />,
      enabled: true,
    },
    {
      key: "org",
      label: "Organization",
      component: <OrgSearch key={orgSearchKey} />,
      enabled: true,
    },
    {
      key: "doc",
      label: "Document",
      component: <DocSearch key={docSearchKey} />,
      enabled: true,
    },
    {
      key: "template",
      label: "Templates",
      component: <TemplateSearch />,
      enabled: true,
    },
    {
      key: "grs",
      label: "GRS",
      component: <GrsSearch />,
      enabled: true,
    },
    {
      key: "publiclibrary",
      label: "Public Library",
      component: <PublicLibrarySearch />,
      enabled: true,
    },
    {
      key: "contentlibrary",
      label: "Content Library",
      component: <ContentLibrarySearch />,
      enabled: true,
    },
    {
      key: "sensor",
      label: "Sensors",
      component: <SensorsSearch />,
      enabled: true,
    },
    {
      key: "media",
      label: "Media",
      component: <MediaSearch />,
      enabled: true,
    },
    // Gated tab kept LAST so that, when the viewer lacks the permission and it
    // is hidden, the positional index of every other tab in <Tabs> is
    // unaffected.
    {
      key: "deletedorgs",
      label: "Deleted orgs",
      component: <DeletedOrgsSearch />,
      enabled: canSearchDeletedOrgs,
    },
  ];

  // Split the scope selector into inline toggle buttons vs. a "+N more" menu.
  // Derived from the *enabled* tabs (not a raw slice) so the counts stay
  // correct even if one of the leading scopes is ever disabled. Each scope
  // keeps its original index so selection/routing is unchanged.
  const enabledScopes = tabs
    .map((tab, index) => ({ ...tab, index }))
    .filter((tab) => tab.enabled);
  const maxInlineScopes = wideEnoughForAllScopes ? enabledScopes.length : 5;
  const inlineScopes = enabledScopes.slice(0, maxInlineScopes);
  const menuScopes = enabledScopes.slice(maxInlineScopes);
  const activeScopeInMenu = menuScopes.some(
    (scope) => scope.index === searchTab,
  );

  const handleTabChange = (
    _event: React.ChangeEvent<unknown>,
    newValue: number,
  ) => {
    setSearchTab(newValue);
    history.push(`/search/${tabs[newValue].key}`);
  };

  const handleScopeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: number | null,
  ) => {
    // ToggleButtonGroup fires with null when the active option is re-clicked.
    if (newValue === null) {
      return;
    }
    setSearchTab(newValue);
    history.push(`/search/${tabs[newValue].key}`);
  };

  const { userSearchQueryState, setUserSearchQueryState } = useContext(
    UserSearchQueryContext,
  );
  const { identitySearchQueryState, setIdentitySearchQueryState } = useContext(
    IdentitySearchQueryContext,
  );
  const { orgSearchQueryState, setOrgSearchQueryState } = useContext(
    OrgSearchQueryContext,
  );
  const { templateSearchQueryState, setTemplateSearchQueryState } = useContext(
    TemplateSearchQueryContext,
  );
  const { grsSearchQueryState, setGrsSearchQueryState } = useContext(
    GrsSearchQueryContext,
  );

  const tabMatch = useRouteMatch<TabMatchParams>("/search/:tab");
  useEffect(() => {
    const tabKey = tabMatch?.params.tab;
    const tabIndex = tabs.findIndex((t) => t.key === tabKey);
    setSearchTab(tabIndex >= 0 ? tabIndex : 0);

    const userId = query.get("userId");
    if (userId) {
      setUserSearchQueryState({
        ...userSearchQueryState,
        userId,
      });
    }
    // Deep link from the Corp Domains page: pre-fill the Users search by email
    // domain and run it (UserSearch searches all regions off the query state).
    const emailDomain = query.get("emailDomain");
    if (emailDomain) {
      setUserSearchQueryState({
        ...userSearchQueryState,
        emailDomain,
      });
    }
    const identityID = query.get("identityid");
    if (identityID) {
      setIdentitySearchQueryState({
        ...identitySearchQueryState,
        identityID,
      });
    }
    const roleId = query.get("roleId");
    if (roleId) {
      if (tabKey === "template") {
        setTemplateSearchQueryState({
          ...templateSearchQueryState,
          orgID: roleId,
        });
      } else if (tabKey === "grs") {
        setGrsSearchQueryState({
          ...grsSearchQueryState,
          orgID: roleId,
        });
      } else {
        setOrgSearchQueryState({
          ...orgSearchQueryState,
          roleId,
        });
      }
    }
    // eslint-disable-next-line
  }, []);

  // Search-scope selector. In the redesign it renders INSIDE each search
  // form's card (so the scope + inputs share one box); for scopes that don't
  // embed it, it falls back to a standalone card above the form.
  const embedScopeKeys = new Set(["user", "org"]);
  const embedActiveScope =
    navRedesign && embedScopeKeys.has(tabs[searchTab]?.key);

  const scopeSelector = (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ flexWrap: "wrap", rowGap: 1 }}
    >
      <Typography
        variant="overline"
        sx={{ color: "text.secondary", fontWeight: 700 }}
      >
        Search for
      </Typography>
      <ToggleButtonGroup
        value={searchTab}
        exclusive
        color="primary"
        size="small"
        onChange={handleScopeChange}
        sx={{
          flexWrap: "wrap",
          bgcolor: "action.hover",
          borderRadius: 2,
          p: 0.5,
          gap: 0.5,
          "& .MuiToggleButtonGroup-grouped": {
            border: 0,
            borderRadius: "8px !important",
            px: 1.5,
            py: 0.5,
            textTransform: "none",
            color: "text.secondary",
            "&.Mui-selected": {
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": { bgcolor: "primary.dark" },
            },
          },
        }}
      >
        {inlineScopes.map((tab) => (
          <ToggleButton key={tab.label} value={tab.index}>
            {tab.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {menuScopes.length > 0 && (
        <>
          <Button
            size="small"
            variant={activeScopeInMenu ? "contained" : "outlined"}
            endIcon={<ExpandMoreIcon />}
            onClick={(event) => setScopeMenuAnchor(event.currentTarget)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {activeScopeInMenu
              ? tabs[searchTab].label
              : `+${menuScopes.length} more`}
          </Button>
          <Menu
            anchorEl={scopeMenuAnchor}
            open={Boolean(scopeMenuAnchor)}
            onClose={() => setScopeMenuAnchor(null)}
          >
            {menuScopes.map((tab) => (
              <MenuItem
                key={tab.label}
                selected={searchTab === tab.index}
                onClick={() => {
                  setScopeMenuAnchor(null);
                  setSearchTab(tab.index);
                  history.push(`/search/${tabs[tab.index].key}`);
                }}
              >
                {tab.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  );

  return (
    <Grid container sx={classes.pageRootSpacing}>
      <Grid container direction={"column"}>
        {!navRedesign ? (
          <Tabs
            value={searchTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
          >
            {tabs.map((tab) => {
              if (tab.enabled) {
                return <Tab label={tab.label} key={tab.label} />;
              }
              return undefined;
            })}
          </Tabs>
        ) : embedActiveScope ? null : (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
            {scopeSelector}
          </Paper>
        )}
        {tabs.map((tab, index) => {
          return (
            <div key={tab.label}>
              <TabPanel value={searchTab} index={index}>
                {navRedesign && embedScopeKeys.has(tab.key)
                  ? React.cloneElement(
                      tab.component as React.ReactElement<{
                        scopeSelector?: React.ReactNode;
                      }>,
                      { scopeSelector },
                    )
                  : tab.component}
              </TabPanel>
            </div>
          );
        })}
      </Grid>
    </Grid>
  );
};

export default Search;
